import React, { useState } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/ReportIssueModal.css';

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(API_ENDPOINTS.ISSUES_REPORT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          user_id: user?._id,
          user_name: user?.name,
          user_email: user?.email,
          company_id: user?.company?._id,
          company_name: user?.company?.name,
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
          setSubmitSuccess(false);
          setFormData({ title: '', description: '', priority: 'medium', category: 'general' });
        }, 2000);
      } else {
        throw new Error('Failed to submit issue');
      }
    } catch (error) {
      console.error('Error submitting issue:', error);
      alert(t('common.error_submitting_issue'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="report-issue-modal-overlay" onClick={onClose}>
      <div className="report-issue-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('common.report_issue')}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {submitSuccess ? (
          <div className="success-message">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>{t('common.issue_submitted_successfully')}</h3>
            <p>{t('common.issue_submitted_description')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="issue-form">
            <div className="form-group">
              <label htmlFor="title">{t('common.issue_title')}</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t('common.issue_title_placeholder')}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">{t('common.issue_category')}</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="general">{t('common.issue_category_general')}</option>
                <option value="bug">{t('common.issue_category_bug')}</option>
                <option value="feature">{t('common.issue_category_feature')}</option>
                <option value="ui_ux">{t('common.issue_category_ui_ux')}</option>
                <option value="performance">{t('common.issue_category_performance')}</option>
                <option value="other">{t('common.issue_category_other')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">{t('common.issue_priority')}</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="low">{t('common.issue_priority_low')}</option>
                <option value="medium">{t('common.issue_priority_medium')}</option>
                <option value="high">{t('common.issue_priority_high')}</option>
                <option value="critical">{t('common.issue_priority_critical')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">{t('common.issue_description')}</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('common.issue_description_placeholder')}
                required
                rows={4}
                className="form-textarea"
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={onClose} className="cancel-button">
                {t('common.cancel')}
              </button>
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? t('common.submitting') : t('common.submit_issue')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportIssueModal;
